/* @jsx h */
import {
  Cart,
  Category,
  FoodType,
  Order,
  Product,
  Service,
  TableStatus,
} from "@lemmonhq/types";
import { defineComponent, Ref, ref, h } from "vue";
import { CartActions } from "./CartActions";
import { CartItems } from "./CartItems";
import { TableOrders } from "./TableOrders";
import { LemmonAccordion } from "@lemmonhq/uikit";

import { CartAPI } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { TablePayment } from "../../pages/Tables/payments/TablePayment";
import { PhoneinOrder } from "./PhoneinOrder";

export interface CartProps {
  cart: Cart;
  onClear: Function;
  onIncrement: (id: string) => void;
  onDecrement: (id: string) => void;
  onRemove: (id: string) => void;
  onUpdateNotes: (notes: { [key: string]: string }) => void;
  services: Service[];
  products: Product[];
  selectedTable: Ref<TableStatus | undefined | null>;
  selectedService: Service | null;
  selectedCategory: Category | null;
  onServiceSelect: Function;
  onCategorySelect: Function;
  onUpdateCart: Function;
  onProductSelect: Function;
  notes: Ref<{ [key: string]: string }>;
  cartAPI: CartAPI;
  isNotesModalOpen: Ref<boolean>;
  isTableSelectModalOpen: Ref<boolean>;
  foodTypes: FoodType[];
}

export type CartInnerProps = CartProps & {
  isLoadingOrders: any;
  tableOrders: Ref<Order[]>;
  isNotesModalOpen: Ref<boolean>;
  isTableSelectModalOpen: Ref<boolean>;
};

export const CartInner = defineComponent<CartInnerProps>({
  name: "CartInner",
  props: [
    "cart",
    "cartAPI",
    "onClear",
    "onIncrement",
    "onDecrement",
    "onRemove",
    "onUpdateNotes",
    "services",
    "products",
    "selectedTable",
    "selectedService",
    "selectedCategory",
    "onServiceSelect",
    "onCategorySelect",
    "onProductSelect",
    "isLoadingOrders",
    "tableOrders",
    "isNotesModalOpen",
    "isTableSelectModalOpen",
    "onUpdateCart",
    "notes",
    "foodTypes",
  ],
  setup(props) {
    const showPaymentModal = ref(false);
    const showPhoneinOrder = ref(false);
    const hiddenClass =
      import.meta.env.VITE_ENV === "production" ? "hidden" : "";
    const { t } = useLocalization();
    const { isNotesModalOpen, isTableSelectModalOpen } = props;
    return () => {
      console.log(
        "rendering cart inner",
        props.selectedTable,
        props.selectedTable.value,
      );
      const sections = [
        {
          id: "cart",
          title: props.selectedTable.value
            ? props.selectedTable.value.table.name +
              (props.selectedTable.value.party?.name
                ? " | " + props.selectedTable.value.party.name
                : "")
            : t("pos.cart.cart"),
          icon: "shopping_cart",
          renderButtons: () => {
            return (
              <div data-training="pos.shortcuts.bar" class="flex space-x-2">
                <button
                  onClick={() =>
                    (props.cartAPI.isAddSeatsModalOpen.value = true)
                  }
                  class={"btn w-[38px] h-[28px] btn-neutral p-0 min-h-0 "}
                >
                  <span class="material-symbols-outlined text-1xl">
                    person_add
                  </span>
                </button>
                <button
                  data-training="pos.shortcuts.table"
                  onClick={() =>
                    (props.cartAPI.isTableSwitcherOpen.value =
                      !props.cartAPI.isTableSwitcherOpen.value)
                  }
                  class={`btn w-[38px] h-[28px] p-0 min-h-0 ${
                    props.cartAPI.isTableSwitcherOpen.value
                      ? "btn-primary"
                      : "btn-neutral"
                  }`}
                >
                  <span class="material-symbols-outlined text-1xl">
                    table_restaurant
                  </span>
                </button>
                <button
                  data-training="pos.shortcuts.phone"
                  onClick={() => {
                    showPhoneinOrder.value = true;
                  }}
                  class="btn w-[38px] h-[28px] btn-neutral p-0 min-h-0"
                >
                  <span class="material-symbols-outlined text-1xl">call</span>
                </button>
                <button
                  data-training="pos.shortcuts.delete"
                  onClick={() => props.onClear()}
                  class="btn w-[38px] h-[28px] btn-error p-0 min-h-0"
                >
                  <span class="material-symbols-outlined text-1xl">delete</span>
                </button>
              </div>
            );
          },
          render: () => (
            <div class="flex-1 flex flex-col overflow-hidden border-b py-2">
              <div class="px-2 flex flex-col flex-1 overflow-auto">
                <CartItems
                  customers={props.cart.customers}
                  cartAPI={props.cartAPI}
                  items={props.cart.items}
                  onIncrement={props.onIncrement}
                  onDecrement={props.onDecrement}
                  foodTypes={props.foodTypes}
                  products={props.products}
                />
              </div>
              <CartActions
                selectedTable={props.selectedTable}
                cart={props.cart}
                onClear={() => props.onClear()}
                onAddNote={() => (isNotesModalOpen.value = true)}
                onSendToTable={() => (isTableSelectModalOpen.value = true)}
              />
            </div>
          ),
        },
        ...(props.selectedTable.value
          ? [
              {
                id: "table",
                title: t("pos.cart.active_orders"),
                icon: "table_restaurant",
                badge: null,
                renderButtons: () => {
                  return (
                    <>
                      <div onClick={() => props.onClear()} class="badge p-2">
                        <span class="material-symbols-outlined text-1xl">
                          order_approve
                        </span>
                      </div>
                    </>
                  );
                },
                render: () => (
                  <div class="flex-1 flex flex-col overflow-hidden p-4">
                    <div class="flex-1 flex flex-col overflow-hidden">
                      <div class="py-2">
                        <button
                          onClick={() => {
                            showPaymentModal.value = true;
                          }}
                          class="btn btn-primary w-full"
                        >
                          <span class="material-symbols-outlined mr-0.5">
                            credit_card
                          </span>
                          {t("general.words.checkout")}
                        </button>
                      </div>
                      <div class="flex flex-col flex-1 overflow-auto">
                        {props.selectedTable.value ? (
                          <TableOrders
                            tableId={props.selectedTable.value.id}
                            reservationId={
                              props.selectedTable.value.party?.reservation_id ??
                              null
                            }
                          />
                        ) : null}
                      </div>
                      {showPaymentModal.value && props.selectedTable.value ? (
                        <TablePayment
                          tableStatus={props.selectedTable.value}
                          onClose={() => {
                            showPaymentModal.value = false;
                          }}
                          onSuccess={(closed) => {
                            // todo: hack for now
                            if (closed) {
                              const t = props.selectedTable.value;
                              props.selectedTable.value = undefined;
                              props.selectedTable.value = t;
                              props.onClear();
                            }
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                ),
              },
            ]
          : []),
      ];
      console.log("sections", sections);
      return (
        <>
          <LemmonAccordion
            items={sections}
            value={props.cartAPI.activeSection}
          />
          {showPhoneinOrder.value && (
            <PhoneinOrder
              cart={props.cart}
              onClose={(success) => {
                if (success) {
                  props.onClear();
                }
                showPhoneinOrder.value = false;
              }}
            />
          )}
        </>
      );
    };
  },
});
